/**
 * Profils de corridor - document 02, section 2.
 *
 * Les noms de champs suivent la spécification à la lettre : `cfaCurrency` et
 * non `currency`, `feeMode` et non `defaultFeeMode`. Un écart ici se paierait à
 * la synchronisation, où le serveur stocke les entités telles quelles.
 */
export const PARITE = 655.957;

/**
 * Profils proposés au premier lancement - document 04, « États et messages ».
 *
 * Les valeurs de la grille Mobile Money sont un POINT DE DÉPART observé, pas une
 * constante : les taxes évoluent, elles sont à revérifier périodiquement.
 *
 * Les identifiants sont FIXES, et ce n'est pas un détail. Avec un UUID tiré au
 * hasard à chaque amorçage, deux amorçages produisent deux jeux distincts au
 * lieu d'un seul :
 *
 *   - en développement, React.StrictMode monte l'effet deux fois et l'on
 *     obtenait quatre profils au lieu de deux ;
 *   - en production, deux appareils amorcés séparément puis synchronisés
 *     auraient également donné quatre profils, sans que rien ne le signale.
 *
 * Un identifiant stable rend l'amorçage idempotent : réécrire le même profil
 * ne fait que le réécrire, et la synchronisation reconnaît la même entité des
 * deux côtés.
 */
export const PROFILS_EXEMPLE = [
  {
    id: 'exemple-xaf-mobile-money',
    name: 'XAF · Mobile Money',
    cfaCurrency: 'XAF',
    officialRate: PARITE,
    rateOut: 655,
    rateIn: 685,
    serviceFixed: 0,
    servicePercent: 2,
    operatorPercent: 0.2,
    operatorFixed: 4,
    operatorCap: 1000000,
    feeMode: 'refacture',
    isDefault: true,
    order: 0,
  },
  {
    id: 'exemple-xof-virement-bancaire',
    name: 'XOF · Virement bancaire',
    cfaCurrency: 'XOF',
    officialRate: PARITE,
    rateOut: 645,
    rateIn: 675,
    serviceFixed: 1.5,
    servicePercent: 1.2,
    operatorPercent: 0,
    operatorFixed: 0,
    operatorCap: 0,
    feeMode: 'acharge',
    isDefault: false,
    order: 1,
  },
];

/** Valeurs d'un profil neuf, raisonnables plutôt que vides. */
export const PROFIL_VIERGE = {
  name: '',
  cfaCurrency: 'XAF',
  officialRate: PARITE,
  rateOut: PARITE,
  rateIn: PARITE,
  serviceFixed: 0,
  servicePercent: 0,
  operatorPercent: 0,
  operatorFixed: 0,
  operatorCap: 0,
  feeMode: 'refacture',
  isDefault: false,
  order: 0,
};

/** Préréglages de grille opérateur, proposés en chips dans le bloc paramètres. */
export const PRESETS_OPERATEUR = [
  { nom: 'Mobile Money', operatorPercent: 0.2, operatorFixed: 4, operatorCap: 1000000 },
  { nom: 'Virement', operatorPercent: 0, operatorFixed: 2500, operatorCap: 0 },
  { nom: 'Aucun', operatorPercent: 0, operatorFixed: 0, operatorCap: 0 },
];

/**
 * Nom du préréglage opérateur correspondant aux paramètres courants, ou null
 * si aucun ne correspond.
 *
 * Comparaison à epsilon plutôt qu'à l'égalité stricte : les valeurs
 * transitent par des champs numériques, et 0,1 + 0,1 ne redonne pas toujours
 * 0,2 en virgule flottante. Un préréglage qui cesse de s'afficher comme retenu
 * après un aller-retour dans un champ serait incompréhensible.
 */
export function presetActif({ operatorPercent, operatorFixed, operatorCap }) {
  const proche = (a, b) => Math.abs(Number(a) - Number(b)) < 1e-9;
  const trouve = PRESETS_OPERATEUR.find((p) =>
    proche(p.operatorPercent, operatorPercent) &&
    proche(p.operatorFixed, operatorFixed) &&
    proche(p.operatorCap, operatorCap));
  return trouve ? trouve.nom : null;
}

/**
 * Les champs qui constituent une CONFIGURATION de cotation.
 *
 * Volontairement distincts de l'entité profil : `name`, `isDefault`, `order` et
 * les horodatages n'entrent pas dans le calcul. C'est cette liste qui décide si
 * les paramètres à l'écran diffèrent du profil chargé, et un champ oublié ici
 * ferait passer une divergence pour une identité.
 */
export const CHAMPS_CONFIG = [
  'officialRate', 'rateOut', 'rateIn',
  'serviceFixed', 'servicePercent',
  'operatorPercent', 'operatorFixed', 'operatorCap',
  'feeMode',
];

/** Extrait la seule configuration d'un profil ou d'un jeu de paramètres. */
export function extraireConfig(source) {
  const config = {};
  for (const cle of CHAMPS_CONFIG) config[cle] = source?.[cle];
  return config;
}

/**
 * Les deux sources décrivent-elles la même configuration ?
 *
 * Comparaison à epsilon sur les nombres, pour la même raison que
 * `presetActif` : les valeurs traversent des champs numériques. Signaler
 * « modifié » sur un écart de 1e-15 invisible à l'écran ferait douter d'un
 * indicateur qui doit rester digne de confiance.
 */
export function memeConfig(a, b) {
  return CHAMPS_CONFIG.every((cle) => {
    const x = a?.[cle];
    const y = b?.[cle];
    if (typeof x === 'number' && typeof y === 'number') return Math.abs(x - y) < 1e-9;
    return x === y;
  });
}

/**
 * Profils partageant un même nom, et lequel garder.
 *
 * Le cas vient d'un défaut d'amorçage corrigé depuis : deux appels concurrents
 * créaient deux jeux de profils d'exemple. Les identifiants sont désormais
 * fixes, mais les doublons déjà créés restent, et un sélecteur qui propose deux
 * fois « XAF · Mobile Money » dont l'un est vierge rend l'application
 * incompréhensible - on croit que changer de profil ne fait rien.
 *
 * Règle de conservation, dans cet ordre :
 *   1. le profil marqué par défaut, s'il y en a un ;
 *   2. sinon celui qui porte le plus de valeurs non triviales, un profil
 *      resté à la parité et sans frais n'ayant jamais servi ;
 *   3. sinon le plus récemment modifié.
 *
 * Rien n'est supprimé ici : cette fonction ne fait que désigner. La suppression
 * reste un geste explicite de l'utilisateur.
 */
export function doublonsDeNom(profils = []) {
  const parNom = new Map();
  for (const p of profils) {
    const cle = (p.name ?? '').trim().toLowerCase();
    if (!parNom.has(cle)) parNom.set(cle, []);
    parNom.get(cle).push(p);
  }

  const groupes = [];
  for (const [, groupe] of parNom) {
    if (groupe.length < 2) continue;
    const garde = [...groupe].sort((a, b) =>
      Number(b.isDefault ?? false) - Number(a.isDefault ?? false) ||
      richesse(b) - richesse(a) ||
      String(b.updatedAt ?? '').localeCompare(String(a.updatedAt ?? '')))[0];
    groupes.push({
      nom: garde.name,
      garde,
      aSupprimer: groupe.filter((p) => p.id !== garde.id),
    });
  }
  return groupes;
}

/** Compte les paramètres réellement renseignés, pour repérer un profil vierge. */
function richesse(p) {
  const significatifs = [
    p.servicePercent, p.serviceFixed,
    p.operatorPercent, p.operatorFixed, p.operatorCap,
    Math.abs((p.rateOut ?? PARITE) - PARITE),
    Math.abs((p.rateIn ?? PARITE) - PARITE),
  ];
  return significatifs.filter((v) => Number(v) > 0).length;
}
